import { useAuth } from "@bcmi-labs/art-auth"
import { Loader as ARTLoader, Spinner } from "@bcmi-labs/art-ui/components"
import { usePaletteScope } from "@bcmi-labs/art-ui/theming"
import type { ComponentProps } from "react"

type LoaderProps = ComponentProps<typeof ARTLoader> & {
  /** Size of the spinner. Only applies when the branded loader is replaced by a spinner. */
  size?: ComponentProps<typeof Spinner>["size"]
}

/**
 * A loader shaped like the Arduino logo, which degrades to a neutral spinner whenever the
 * Arduino branding isn't appropriate:
 * - the space is customized (whitelabel/custom domain environments);
 * - a custom palette is applied, so the logo would be tinted with a non-Arduino color.
 *
 * The palette is read from the nearest `PaletteScope` (falling back to the root one), so a subtree
 * scoped to a custom palette gets the spinner even when the space itself isn't customized.
 *
 * Always use this instead of `Loader` from `art-ui` (enforced by the `cloud-website/no-raw-loader` ESLint rule).
 */
export function Loader(props: LoaderProps) {
  const { size, ...rest } = props
  const { client } = useAuth()
  const paletteScope = usePaletteScope()

  const isCustomized = Boolean(client.getCustomization()) || paletteScope.startsWith("custom-")

  if (isCustomized) return <Spinner size={size} {...rest} />

  return <ARTLoader {...rest} />
}
